// Copied from kettle-platform workers/admin/ui/src/charts/Chart.tsx (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// Single-series daily bar chart: thin bars, 4px rounded tops on the baseline, a 2px gap,
// recessive axis, hover/focus tooltip per bar, and a table view for the same numbers.
import { useContext, useId, useMemo, useState } from "react";
import { MarksContext } from "./Lines";
import { fmt } from "../fmt";
import { niceMax } from "./scale";

export interface Point {
  day: string;
  value: number;
}

const W = 640;
const H = 180;
const PAD = { l: 44, r: 8, t: 12, b: 24 };

export function DailyBars({ title, points, unit, format = fmt.compact, span, empty }: { title: string; points: Point[]; unit: string; format?: (n: number) => string; span?: string; empty?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const marks = useContext(MarksContext);
  const id = useId();
  const max = useMemo(() => niceMax(Math.max(0, ...points.map((p) => p.value))), [points]);
  const total = points.reduce((s, p) => s + p.value, 0);
  const n = Math.max(points.length, 1);
  const slot = (W - PAD.l - PAD.r) / n;
  const bw = Math.max(2, Math.min(18, slot - 2));
  const y = (v: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / max);
  const ticks = [0, max / 2, max];
  const shown = hover !== null ? points[hover] : null;

  return (
    <figure className="chart" aria-labelledby={`${id}-t`}>
      <figcaption>
        <div>
          <span id={`${id}-t`} className="chart-title">{title}</span>
          <span className="chart-total">
            {shown ? (
              <>
                <strong>{format(shown.value)}</strong> {unit} on {shown.day}
              </>
            ) : (
              <>
                <strong>{format(total)}</strong> {unit} {span ?? `in ${points.length} days`}
              </>
            )}
          </span>
        </div>
        <button className="linkish" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
          {asTable ? "Chart" : "Table"}
        </button>
      </figcaption>
      {points.length === 0 ? (
        <p className="empty">{empty ?? "No data yet. The collector runs every hour."}</p>
      ) : asTable ? (
        <div className="table-wrap small">
          <table>
            <thead>
              <tr><th>Day</th><th className="num">{unit}</th></tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p) => (
                <tr key={p.day}><td>{p.day}</td><td className="num">{fmt.int(p.value)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: daily values for ${points.length} days`} onPointerLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} />
              <text className="axis" x={PAD.l - 6} y={y(t) + 4} textAnchor="end">{fmt.compact(t)}</text>
            </g>
          ))}
          {points.map((p, i) => {
            const x = PAD.l + i * slot + (slot - bw) / 2;
            const top = y(p.value);
            const h = Math.max(0, H - PAD.b - top);
            const r = Math.min(4, bw / 2, h);
            return (
              <g key={p.day}>
                <path
                  className={`bar${hover === i ? " on" : ""}`}
                  d={h <= 0 ? "" : `M${x},${H - PAD.b}V${top + r}Q${x},${top} ${x + r},${top}H${x + bw - r}Q${x + bw},${top} ${x + bw},${top + r}V${H - PAD.b}Z`}
                />
                <rect
                  className="hit"
                  x={PAD.l + i * slot}
                  y={PAD.t}
                  width={slot}
                  height={H - PAD.t - PAD.b}
                  tabIndex={0}
                  aria-label={`${p.day}: ${fmt.int(p.value)} ${unit}`}
                  onPointerEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
          {marks.map((m) => {
            const i = points.findIndex((p) => p.day === m.day);
            if (i < 0) return null;
            const cx = PAD.l + i * slot + slot / 2;
            return (
              <g key={`${m.day}-${m.label}`} className={`chart-mark ${m.kind ?? "release"}`}>
                <path d={`M${cx} ${PAD.t - 8}l4 4-4 4-4-4z`} />
                <title>{`${m.day}: ${m.label}`}</title>
              </g>
            );
          })}
          <line className="baseline" x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} />
          {points.length > 0 && (
            <>
              <text className="axis" x={PAD.l} y={H - 6}>{points[0]!.day.slice(5)}</text>
              <text className="axis" x={W - PAD.r} y={H - 6} textAnchor="end">{points[points.length - 1]!.day.slice(5)}</text>
            </>
          )}
        </svg>
      )}
    </figure>
  );
}

/** Fills missing days with 0 so bars line up by date. */
export function lastDays(series: { day: string; metric: string; value: number }[], metric: string, days = 30, now = Date.now()): Point[] {
  const by = new Map(series.filter((s) => s.metric === metric).map((s) => [s.day, s.value]));
  const out: Point[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const day = new Date(now - i * 86_400_000).toISOString().slice(0, 10);
    out.push({ day, value: by.get(day) ?? 0 });
  }
  return out;
}
